import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ZoomIn, ZoomOut, RotateCw, AlertTriangle, Plus, Trash2,
  CheckCircle2, X, ChevronLeft,
} from 'lucide-react';
import { clsx } from 'clsx';

// ── Types ──────────────────────────────────────────────────────────

interface DrugEntry {
  id:          number;
  drug_name:   string;
  dose:        string;
  frequency:   string;
  duration:    string;
  confidence?: number;  // 0–1 from OCR engine; undefined = manually added
}

// ── Helpers ────────────────────────────────────────────────────────

const FREQUENCY_OPTIONS = ['OD','BD','TDS','QID','SOS','Stat','Before meals','After meals','At bedtime'];
const DURATION_OPTIONS  = ['1 day','3 days','5 days','7 days','10 days','14 days','1 month','As directed'];

let nextId = 100;

function isLowConfidence(confidence?: number): boolean {
  return confidence !== undefined && confidence < 0.70;
}

// ── Sub-components ─────────────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence === undefined) return null;
  const pct = Math.round(confidence * 100);
  const low = confidence < 0.70;
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 text-2xs font-medium px-1.5 py-0.5 rounded-full',
      low
        ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
        : 'bg-green-100 text-green-700 border border-green-200',
    )}>
      {low && <AlertTriangle size={9}/>}
      {pct}%
    </span>
  );
}

function DrugRow({
  entry,
  onChange,
  onRemove,
}: {
  entry:    DrugEntry;
  onChange: (id: number, key: keyof DrugEntry, val: string) => void;
  onRemove: (id: number) => void;
}) {
  const low    = isLowConfidence(entry.confidence);
  const border = low ? 'border-yellow-400 bg-yellow-50/40' : 'border-gray-200';

  return (
    <div className={clsx(
      'rounded-xl border-2 p-4 space-y-3 transition-colors',
      border,
    )}>
      {/* Row header */}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Drug Name
            </label>
            <ConfidenceBadge confidence={entry.confidence}/>
            {low && (
              <span className="text-xs text-yellow-700 font-medium flex items-center gap-1">
                <AlertTriangle size={11}/>
                Verify manually
              </span>
            )}
          </div>
          <input
            value={entry.drug_name}
            onChange={e => onChange(entry.id, 'drug_name', e.target.value)}
            placeholder="Generic or brand name"
            className={clsx(
              'w-full border rounded-lg px-3 py-2 text-sm font-medium focus:outline-none transition',
              low
                ? 'border-yellow-400 focus:border-yellow-500 bg-yellow-50/50'
                : 'border-gray-200 focus:border-navy',
            )}
          />
        </div>
        <button
          onClick={() => onRemove(entry.id)}
          aria-label="Remove this drug"
          className="self-start mt-6 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition flex-shrink-0"
        >
          <Trash2 size={14}/>
        </button>
      </div>

      {/* Dose / Frequency / Duration */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block font-medium">Dose</label>
          <input
            value={entry.dose}
            onChange={e => onChange(entry.id, 'dose', e.target.value)}
            placeholder="e.g. 500mg"
            className={clsx(
              'w-full border rounded-lg px-2.5 py-1.5 text-xs focus:outline-none transition',
              low
                ? 'border-yellow-400 focus:border-yellow-500 bg-yellow-50/50'
                : 'border-gray-200 focus:border-navy',
            )}
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block font-medium">Frequency</label>
          <select
            value={entry.frequency}
            onChange={e => onChange(entry.id, 'frequency', e.target.value)}
            className={clsx(
              'w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-none transition',
              low
                ? 'border-yellow-400 focus:border-yellow-500 bg-yellow-50/50'
                : 'border-gray-200 focus:border-navy',
            )}
          >
            {FREQUENCY_OPTIONS.map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block font-medium">Duration</label>
          <select
            value={entry.duration}
            onChange={e => onChange(entry.id, 'duration', e.target.value)}
            className={clsx(
              'w-full border rounded-lg px-2 py-1.5 text-xs focus:outline-none transition',
              low
                ? 'border-yellow-400 focus:border-yellow-500 bg-yellow-50/50'
                : 'border-gray-200 focus:border-navy',
            )}
          >
            {DURATION_OPTIONS.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

// ── Image Viewer ────────────────────────────────────────────────────

function ImageViewer({ src }: { src: string }) {
  const [zoom,    setZoom]    = useState(1);
  const [rotate,  setRotate]  = useState(0);
  const containerRef          = useRef<HTMLDivElement>(null);

  function zoomIn()  { setZoom(z => Math.min(z + 0.25, 4)); }
  function zoomOut() { setZoom(z => Math.max(z - 0.25, 0.5)); }
  function rotateCw(){ setRotate(r => (r + 90) % 360); }
  function reset()   { setZoom(1); setRotate(0); }

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50 flex-shrink-0">
        <button
          onClick={zoomOut}
          disabled={zoom <= 0.5}
          aria-label="Zoom out"
          className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-navy/30 disabled:opacity-40 transition"
        >
          <ZoomOut size={14}/>
        </button>
        <button
          onClick={zoomIn}
          disabled={zoom >= 4}
          aria-label="Zoom in"
          className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-navy/30 disabled:opacity-40 transition"
        >
          <ZoomIn size={14}/>
        </button>
        <span className="text-xs font-mono text-gray-400 w-10 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <div className="h-4 w-px bg-gray-200"/>
        <button
          onClick={rotateCw}
          aria-label="Rotate 90° clockwise"
          className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-navy hover:border-navy/30 transition"
        >
          <RotateCw size={14}/>
        </button>
        <button
          onClick={reset}
          className="ml-auto px-2.5 py-1 text-xs rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-navy transition"
        >
          Reset
        </button>
      </div>

      {/* Image pan / scroll area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto bg-gray-100 flex items-start justify-center p-4 cursor-grab active:cursor-grabbing"
        style={{ scrollbarWidth: 'thin' }}
      >
        {src ? (
          <img
            src={src}
            alt="Scanned prescription"
            draggable={false}
            style={{
              transform: `scale(${zoom}) rotate(${rotate}deg)`,
              transformOrigin: 'top center',
              transition:      'transform 0.15s ease',
              maxWidth:        'none',
              borderRadius:    '4px',
              boxShadow:       '0 2px 16px rgba(0,0,0,0.15)',
            }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-300 gap-3">
            <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
            </div>
            <p className="text-sm">No scan image provided</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────

export default function OcrVerifyPage() {
  const navigate      = useNavigate();
  const [params]      = useSearchParams();

  // In production these would come from props/query params or an API call
  // keyed by encounterId. Here we accept them as query params for flexibility.
  const encounterId   = params.get('encounterId') ?? '';
  const scanImageUrl  = params.get('imageUrl') ?? '';
  const patientName   = params.get('patient')  ?? 'Unknown Patient';
  const patientUid    = params.get('uid')       ?? '—';

  // OCR-extracted drugs — in production, fetched from the backend OCR result
  const [drugs, setDrugs] = useState<DrugEntry[]>([
    // Populated from API in real use; shown here as demo placeholders
    { id: 1, drug_name: 'Tab. Amoxicillin',  dose: '500mg', frequency: 'TDS', duration: '5 days',  confidence: 0.92 },
    { id: 2, drug_name: 'Cap. Omeprazole',   dose: '20mg',  frequency: 'BD',  duration: '7 days',  confidence: 0.61 },
    { id: 3, drug_name: 'Tab. Paracetamol',  dose: '650mg', frequency: 'SOS', duration: '3 days',  confidence: 0.88 },
  ]);

  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);

  const lowConfidenceCount = drugs.filter(d => isLowConfidence(d.confidence)).length;

  function updateDrug(id: number, key: keyof DrugEntry, val: string) {
    setDrugs(prev => prev.map(d => d.id === id ? { ...d, [key]: val } : d));
  }

  function removeDrug(id: number) {
    setDrugs(prev => prev.filter(d => d.id !== id));
  }

  function addDrug() {
    setDrugs(prev => [
      ...prev,
      { id: nextId++, drug_name: '', dose: '', frequency: 'BD', duration: '5 days' },
    ]);
  }

  async function handleConfirm() {
    setSubmitting(true);
    try {
      // POST to backend — replace with actual API call
      // await api.post('/prescriptions/from-ocr', { encounterId, drugs });
      await new Promise(r => setTimeout(r, 800)); // simulate network
      setSubmitted(true);
      setTimeout(() => navigate(-1), 1200);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3 text-green-600">
          <CheckCircle2 size={48} strokeWidth={1.5}/>
          <p className="text-lg font-semibold">Prescription sent to Pharmacy</p>
          <p className="text-sm text-gray-400">Redirecting…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 overflow-hidden">

      {/* ── Page header ──────────────────────────────────────────────── */}
      <header className="flex items-center gap-4 px-6 py-3.5 bg-white border-b border-gray-200 flex-shrink-0 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-navy transition"
        >
          <ChevronLeft size={18}/>
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-navy font-semibold text-base leading-tight">
            OCR Prescription Verification
          </h1>
          <p className="text-xs text-gray-400 truncate mt-0.5">
            {patientName}
            {patientUid !== '—' && <span className="ml-2 font-mono">{patientUid}</span>}
            {encounterId && <span className="ml-2 text-gray-300">· {encounterId.slice(-8).toUpperCase()}</span>}
          </p>
        </div>

        {/* Low confidence warning badge */}
        {lowConfidenceCount > 0 && (
          <div className="flex items-center gap-1.5 bg-yellow-50 border border-yellow-300 text-yellow-700 text-xs font-medium px-3 py-1.5 rounded-full flex-shrink-0">
            <AlertTriangle size={13}/>
            {lowConfidenceCount} field{lowConfidenceCount > 1 ? 's' : ''} need review
          </div>
        )}
      </header>

      {/* ── Split body ───────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── LEFT — Image viewer (50%) ─────────────────────────────── */}
        <div className="w-1/2 flex flex-col border-r border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-white border-b border-gray-100 flex-shrink-0">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
              Scanned Prescription
            </h2>
          </div>
          <div className="flex-1 overflow-hidden">
            <ImageViewer src={scanImageUrl}/>
          </div>
        </div>

        {/* ── RIGHT — Drug verification form (50%) ─────────────────── */}
        <div className="w-1/2 flex flex-col overflow-hidden">
          <div className="px-5 py-3 bg-white border-b border-gray-100 flex-shrink-0 flex items-center justify-between">
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
              Extracted Drugs
            </h2>
            <span className="text-xs text-gray-400">{drugs.length} item{drugs.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Scrollable drug list */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
               style={{ paddingBottom: '80px' }}> {/* space for sticky footer */}

            {drugs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-300 gap-3">
                <div className="w-12 h-12 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center">
                  <X size={20}/>
                </div>
                <p className="text-sm">No drugs extracted — add manually</p>
              </div>
            ) : (
              drugs.map(drug => (
                <DrugRow
                  key={drug.id}
                  entry={drug}
                  onChange={updateDrug}
                  onRemove={removeDrug}
                />
              ))
            )}

            {/* Add drug */}
            <button
              onClick={addDrug}
              className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:text-navy hover:border-navy/30 transition flex items-center justify-center gap-2"
            >
              <Plus size={14}/>
              Add Drug
            </button>
          </div>
        </div>
      </div>

      {/* ── Sticky action bar ──────────────────────────────────────────── */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between gap-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] z-10">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition font-medium"
        >
          Cancel
        </button>

        <div className="flex items-center gap-3">
          {lowConfidenceCount > 0 && (
            <p className="text-xs text-yellow-600 flex items-center gap-1.5">
              <AlertTriangle size={12}/>
              Please verify highlighted fields before confirming
            </p>
          )}
          <button
            onClick={handleConfirm}
            disabled={submitting || drugs.length === 0}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-orange text-gray-900 text-sm font-semibold hover:bg-orange-dark disabled:opacity-50 transition shadow-sm"
          >
            <CheckCircle2 size={15}/>
            {submitting ? 'Sending…' : 'Confirm & Send to Pharmacy'}
          </button>
        </div>
      </div>
    </div>
  );
}
